import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { UserTimezoneInfo } from '../../../../AngularJS/Admin/timezone.model';
import { CVLocalizationService } from '../../../reusables/services/cv-localization.service';
import { TimezoneService } from '../../../reusables/services/timezone.service';
import { MultiFactorService } from '../multi-factor.service';
import { AddEditModuleAccessByLoginParameters, GroupDTO, GroupTypeDTO, Language, LDGroupInfo, Permissions, ProfileDTO, UpdateUserDTO, User, UserProfileDTO } from '../user-admin.model';
import { UserAdminService } from '../user-admin.service';

@Component({
    selector: 'cv-details-useradmin',
    templateUrl: './details-useradmin.component.html',
    styleUrls: ['./details-useradmin.component.scss']
})
export class DetailsUseradminComponent implements OnInit {
    @Input() user: User;
    @Input() userProfile: UserProfileDTO;
    @Input() set langs(langs: Language[]) {
        this.languages = langs;
        this.language = this.languages.find(f => f.Key == this.user.LocalizationSetting)?.Value ?? "";
    }
    @Input() profileDTOs: ProfileDTO[];
    @Input() baseGroupTypes: string[] = [];
    @Input() groupNames: GroupTypeDTO[] = [];
    @Input() ldGroups: LDGroupInfo[] = [];
    @Output() onSave: EventEmitter<void> = new EventEmitter<void>();
    @Output() updateUser: EventEmitter<User> = new EventEmitter<User>();

    public localizationDic: Map<string, string>;
    public permissions: Permissions;
    public timezoneInfo: UserTimezoneInfo = this.timezoneService.dataObj;
    public languages: Language[];
    public language: string = "NoneSet";
    public updatingUser: boolean = false;
    public editing: boolean = false;
    public editUser: User;
    public editUserProfile: UserProfileDTO;
    public editLanguage: string = "NoneSet";
    public noDataGroupMessage: string = "";
    public resettingMfaAuthenticator: boolean = false;
    public unlockingAccount: boolean = false;
    public resettingPassword: boolean = false;
    public groupLevelOptions: GroupTypeDTO[] = [];
    public ldGroupOptions: GroupDTO[] = [];
    public previousProfileID: number;

    constructor(
        private uaService: UserAdminService,
        private locService: CVLocalizationService,
        private timezoneService: TimezoneService,
        private mFSerice: MultiFactorService
    ) {
        this.constructorAsync();
        this.timezoneService.getUserTimezoneInfo
    }

    ngOnInit(): void {
    }

    async constructorAsync() {
        this.localizationDic = await this.locService.GetSection("Users");
        this.permissions = this.uaService.dataObj.permissions ?? await this.uaService.getLoginUserPermissions();
    }

    createOrWriteAccess(): boolean {
        return this.permissions?.CreateAccess || this.permissions?.WriteAccess;
    }

    async unlockAccount(loginID: number) {
        if (this.unlockingAccount || !loginID || loginID < 1)
            return;
        this.unlockingAccount = true;

        try {
            await this.uaService.unlockAccount(loginID);
        }
        catch (err) {
            showErrorMessage(err);
        }
        this.unlockingAccount = false;

        this.uaService.unlockAccount(loginID).then(() => {
            this.user.AccountLocked = false;
        }).catch((response) => {
            showErrorMessage(response);
        }).finally(() => {
            this.unlockingAccount = false;
        });

        this.onSave.emit();

        this.cancelEdit();
    }

    resetPassword(loginID: number) {
        if (this.resettingPassword || !loginID || loginID < 1)
            return;
        this.resettingPassword = true;

        this.uaService.resetPassword(loginID).then(() => {
            this.user.AccountLocked = false;
        }).catch((response) => {
            showErrorMessage(response);
        }).finally(() => {
            this.resettingPassword = false;
        });
    }

    resetMfaAuthenticator() {
        if (this.resettingMfaAuthenticator || !this.user)
            return;
        this.resettingMfaAuthenticator = true;
        this.mFSerice.postResetAuthenticator(this.user.LoginID).then(() => { }).catch(() => {
            showErrorMessage("Could not reset authenticator");
        }).finally(() => {
            this.resettingMfaAuthenticator = false;
        });
    }

    checkLDGroup() {
        if (this.editUser.RelatedGroupName) {
            let index: number = this.ldGroups.findIndex(f => f.Key == this.editUser.RelatedGroupLevelName
                && f.Value[0].BaseGroupType == this.editUser.RelatedGroupType);
            let msg = "";
            if (index > -1) {
                msg = this.localizationDic.get("Data_Group_Inactive")
                    .replace("{0}", "\"" + this.editUser.RelatedGroupName + "\"").trim();
                for (var i = 0; i < this.ldGroups[index].Value.length; i++) {
                    if (this.ldGroups[index].Value[i].GroupID == this.editUser.RelatedGroupID) {
                        //group selected
                        msg = "";
                        this.noDataGroupMessage = msg;
                        break;
                    }
                }
                if (msg != "") {
                    this.noDataGroupMessage = msg;
                }
            }
        }
    }

    editUserBtn() {
        this.editing = true;
        this.editUser = JSON.parse(JSON.stringify(this.user));
        this.previousProfileID = this.editUser.ProfileID;
        this.editUserProfile = JSON.parse(JSON.stringify(this.userProfile));
        this.editUserProfile.DateLevelEarned = (!!this.userProfile.DateLevelEarned) ? new Date(this.userProfile.DateLevelEarned) : undefined;
        this.editLanguage = this.language;
        this.editUser.MultiFactorRequired = (this.editUser.MultiFactorStatus > 1) ? true : false;
        this.checkLDGroup();
        this.updateGroupLevelOptions();
        this.updateLdGroups();
    }

    cancelEdit() {
        this.language = this.editLanguage;
        this.editUser = undefined;
        this.editUserProfile = undefined;
        this.editLanguage = "";
        this.noDataGroupMessage = "";
        this.editing = false;
    }

    saveDisabled(): boolean {
        return this.updatingUser || !this.editUser.LoginName || this.editUser.LoginName.trim() == "" || this.editUser.Email.trim() == ""
            || this.noDataGroupMessage != "";
    }

    async saveChanges() {
        if (this.saveDisabled() || !this.createOrWriteAccess() || this.noDataGroupMessage != "") return;

        this.updatingUser = true;

        let newMultiFactorStatus = this.user.MultiFactorStatus;
        switch (this.editUser.MultiFactorStatus) {
            case 0: // Original status was Inactive, Not Required
            case 1: // Original status was Active, Not Required
                if (this.editUser.MultiFactorRequired)
                    newMultiFactorStatus = 2; // Active & Required
                // else keep original active/inactive, not required
                break;
            case 2: // Original status was Active & Required
                if (!this.editUser.MultiFactorRequired)
                    newMultiFactorStatus = 1; // Active, Not Required
                // else still Active & Required.
                break;
            // case 3: Required by Security Policy, don't change anything    
        }
        this.editUser.MultiFactorStatus = newMultiFactorStatus;

        this.editUser.LocalizationSetting = this.languages?.find(f => f.Value == this.editLanguage)?.Key ?? 0;
        this.editUser.ProfileID = this.profileDTOs?.find(f => f.ProfileName == this.editUser.ProfileName)?.ProfileID;

        let updateUser: UpdateUserDTO = new UpdateUserDTO();
        Object.assign(updateUser, this.editUserProfile, this.editUser);

        let success: boolean = true;
        try {
            await this.uaService.commitUserChanges(updateUser);
        }
        catch (err) {
            success = false;
            console.error("Could not save changes to the user:", err);
            let errorMessage = "An error has occurred.";
            if (err && err.status_code == 400 && err.warnings.length && err.warnings[0])
                errorMessage = htmlEncode(err.warnings[0]);
            showErrorMessage(errorMessage, "Error");
        }

        if (success) {
            this.user = JSON.parse(JSON.stringify(this.editUser));
            this.userProfile = JSON.parse(JSON.stringify(this.editUserProfile));
            this.userProfile.DateLevelEarned = new Date(this.editUserProfile.DateLevelEarned);

            if(this.editUser.ProfileID != this.previousProfileID){
                await this.removeUserAccess();
                this.previousProfileID = this.editUser.ProfileID;
            }

            this.updateUser.emit(this.editUser)
            this.onSave.emit();
            this.cancelEdit();
        }

        this.updatingUser = false;
    }

    updateGroupLevelOptions() {
        this.groupLevelOptions = [];
        this.groupNames.forEach(e => {
            if (e.BaseGroupType == this.editUser.RelatedGroupType)
                this.groupLevelOptions.push(e);
        });
    }

    resetGroupLevel() {
        this.editUser.RelatedGroupLevel = -1;
        this.editUser.RelatedGroupLevelName = "";
        this.updateGroupLevelOptions();
    }

    onRelatedGroupTypeChange(event) {
        this.editUser.RelatedGroupType = this.editUser.RelatedGroupTypeName == "Employee" ? 0 : 1;

        this.resetGroupLevel();
        this.resetLDGroup();
    }

    updateLdGroups() {
        this.ldGroupOptions = [];
        this.ldGroups.forEach(ldGroup => {
            if (ldGroup.Key == this.editUser.RelatedGroupLevelName && ldGroup.Value.length > 0 && ldGroup.Value[0].BaseGroupType == this.editUser.RelatedGroupType) {
                ldGroup.Value.forEach(group => {
                    this.ldGroupOptions.push(group);
                });
            }
        });
    }

    resetLDGroup() {
        this.editUser.RelatedGroupID = -1;
        this.editUser.RelatedGroupName = "";
        this.editUser.ParentGroupID = -1;
        this.editUser.ParentGroupName = "";
        this.updateLdGroups();
    }
    onRelatedGroupLevelChange(event) {
        let groupLevel: string = event.target.value;

        this.editUser.RelatedGroupLevel = this.groupLevelOptions.find(f => f.GroupTypeName == groupLevel).GroupLevel;

        this.resetLDGroup();
    }

    onLDGroupChange(event) {
        let group: GroupDTO = this.ldGroupOptions.find(f => f.GroupName == this.editUser.RelatedGroupName);
        this.editUser.RelatedGroupID = group.GroupID;
        this.editUser.ParentGroupID = group.ParentGroupID;
        this.editUser.ParentGroupName = "N/A";
        this.ldGroups.forEach((LDGroup) => {
            if (LDGroup.Value[0] != null)
                if (LDGroup.Value[0].GroupLevel == this.editUser.RelatedGroupLevel + 1)
                    if (LDGroup.Value[0].BaseGroupType == this.editUser.RelatedGroupType) {
                        for (let i = 0; i < LDGroup.Value.length; ++i) {
                            if (LDGroup.Value[i].GroupID == this.editUser.ParentGroupID) {
                                this.editUser.ParentGroupName = LDGroup.Value[i].GroupName;
                                break;
                            }
                        }
                        return;
                    }
        });
        this.checkLDGroup();
    }

    public async removeUserAccess(){
        await this.uaService.removeModuleAccessByLoginID(this.user.LoginID);
        await this.uaService.removePageAccessByLoginID(this.user.LoginID);
    }
}
